import { Flex } from '@percent/lemonade'
import { ProfileHeader } from './components/ProfileHeader'
import { ContactIdentityCard } from './components/ContactIdentityCard'
import { LinkedEntityCard } from './components/LinkedEntityCard'
import { AdminCard } from './components/AdminCard'
import { SecurityVerificationCard } from './components/SecurityVerificationCard'
import { RolesPermissionsCard } from './components/RolesPermissionsCard'
import { EffectiveRolesCard } from './components/EffectiveRolesCard'
import { useMemberProfileActions } from './components/useMemberProfileActions'
import type { MemberProfileProps } from './MemberProfile.types'

export function MemberProfile({
  account,
  memberType,
  partnerName,
  organisationName,
  permissions,
  effectiveRoles,
  canChangeRole,
  isStrictSSOPartner,
  onRefresh
}: MemberProfileProps) {
  // Actions live next to the data they act on: lifecycle in the header, 2FA in
  // the security card, remove-from-partner in the partner card, remove-admin in
  // the admin card. The hook owns the modal state; `modals` is rendered once.
  const { modals, headerActions, securityActions, partnerActions, adminActions } = useMemberProfileActions({
    account,
    memberType,
    isStrictSSOPartner,
    onRefresh
  })

  return (
    <Flex direction="column" gap="md" data-testid="member-profile">
      <ProfileHeader account={account} memberType={memberType} actions={headerActions} />
      <Flex direction="row" gap="md" align="flex-start">
        <Flex direction="column" gap="md" style={{ flex: 1, minWidth: 0 }}>
          <ContactIdentityCard account={account} />
          {account.organisationId && (
            <LinkedEntityCard type="organisation" entityId={account.organisationId} entityName={organisationName} />
          )}
          {account.partnerId && (
            <LinkedEntityCard
              type="partner"
              entityId={account.partnerId}
              entityName={partnerName}
              action={partnerActions}
            />
          )}
          {account.admin && <AdminCard account={account} action={adminActions} />}
          <SecurityVerificationCard account={account} action={securityActions} />
        </Flex>
        {/* A general (typeless / non-internal) account has no roles or
            permissions surface — show identity/contact/security only. */}
        {memberType !== 'general' && (
          <Flex direction="column" gap="md" style={{ flex: 1, minWidth: 0 }}>
            {effectiveRoles && <EffectiveRolesCard {...effectiveRoles} />}
            <RolesPermissionsCard
              account={account}
              memberType={memberType}
              permissions={permissions}
              canChangeRole={canChangeRole}
              onRefresh={onRefresh}
            />
          </Flex>
        )}
      </Flex>
      {modals}
    </Flex>
  )
}
